import { useEffect, useState, useContext } from "react";
import styles from "./styles.module.css";
import { ReactComponent as EditIcon } from "../../../images/svg/edit.svg";
import { ReactComponent as SaveIcon } from "../../../images/svg/tick-circle.svg";
import Tooltip from "@mui/material/Tooltip";
import SkeletonLoading from "../../utils/Loading/SkeletonLoading";
import * as InvoiceHelper from "helpers/InvoiceHelper";
import { roundOff } from "helpers/NumberFormatHelper";
import { TerminalContext } from "../../../context/TerminalContext";
import * as TerminalHelper from "helpers/TermialHelper";

const SAMPLE_ITEMS = [
  { id: 1, name: "Orange Juice", qty: 1, rate: 80 },
  { id: 2, name: "Mango Juice", qty: 2, rate: 80 },
];

function InvoiceSample({ terminalId }) {
  const { fetchInvoiceFormat, setTerminalInvoiceFormat } = useContext(TerminalContext);
  const [formData, setFormData] = useState({
    shop_name: "",
    address_1: "",
    address_2: "",
    address_3: "",
    address_4: "",
    header: "",
    tel_no: "",
    tin: "",
    note: "",
    footer_1: "",
    footer_2: "",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [items] = useState(SAMPLE_ITEMS);

  const terminalSettings = TerminalHelper.getTerminalSettings();

  const upiId = terminalSettings?.upi_id;

  useEffect(() => {
    if (!terminalId) return;
    let mounted = true;

    const invoiceFormat = InvoiceHelper.getInvoiceFormatForTerminal(terminalId);
    if (invoiceFormat) {
      setFormData(invoiceFormat);
    }

    (async () => {
      setLoading(true);
      try {
        const data = await fetchInvoiceFormat(terminalId);

        if (!mounted || !data) return;

        setFormData(data);
      } catch (err) {
        console.error("Failed to load bill format:", err);
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [terminalId, fetchInvoiceFormat]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const toggleEdit = () => setIsEditing((s) => !s);

  const handleSave = async () => {
    setLoading(true);
    try {
      setSaving(true);

      await InvoiceHelper.saveInvoiceFormatForTerminal(terminalId, formData);
      setTerminalInvoiceFormat(formData);

      setIsEditing(false);
    } catch (err) {
      console.error("Error updating invoice format:", err);
    } finally {
      setSaving(false);
      setLoading(false);
    }
  };

  const subtotal = items.reduce((s, it) => s + it.qty * it.rate, 0);
  const cgst = 18;
  const sgst = 18;
  const cess = 9;
  const rounding = -2;
  const total = subtotal + cgst + sgst + cess + rounding;

  return (
    <>
      {loading ? (
        <SkeletonLoading isOpen={loading} count={1} height={578} xs={12} md={12} sm={12} lg={12} />
      ) : (
        <div className={styles.card}>
          <div className={styles.headerRow}>
            <div />
            <div className={styles.iconWrap}>
              {isEditing ? (
                <Tooltip title="Save Changes" arrow>
                  <span>
                    <SaveIcon
                      onClick={handleSave}
                      disabled={saving}
                      className={styles.icon}
                      style={{ cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.6 : 1 }}
                    />
                  </span>
                </Tooltip>
              ) : (
                <Tooltip title="Edit" arrow>
                  <EditIcon onClick={toggleEdit} className={styles.icon} />
                </Tooltip>
              )}
            </div>
          </div>

          <table className={styles.unifiedHeader}>
            <tbody>
              <tr>
                <td colSpan="4" className={styles.sellerCell}>
                  <div
                    className={`${styles.addressRow} ${isEditing ? styles.addressRowEditing : styles.addressRowView}`}
                  >
                    {isEditing ? (
                      <>
                        <label className={styles.inputLabel}>Shop name :</label>
                        <input
                          className={styles.inputAddress}
                          style={{ fontSize: "18px", fontWeight: "700" }}
                          name="shop_name"
                          value={formData.shop_name}
                          onChange={handleChange}
                          placeholder="Shop name"
                        />
                      </>
                    ) : (
                      <h2 className={styles.shopTitle}>{formData.shop_name}</h2>
                    )}
                  </div>

                  <div>
                    {["address_1", "address_2", "address_3", "address_4"].map((k, idx) => {
                      const val = formData[k];
                      if (!isEditing && !val) return null;

                      return (
                        <div
                          key={k}
                          className={`${styles.addressRow} ${isEditing ? styles.addressRowEditing : styles.addressRowView}`}
                        >
                          {isEditing ? (
                            <>
                              <label className={styles.inputLabel}>Address {idx + 1}:</label>
                              <input
                                className={styles.inputAddress}
                                name={k}
                                value={val}
                                onChange={handleChange}
                                placeholder={`Enter Address ${idx + 1}`}
                              />
                            </>
                          ) : (
                            <span className={styles.content}>{val}</span>
                          )}
                        </div>
                      );
                    })}

                    {(isEditing || formData.tel_no) && (
                      <div
                        className={`${styles.addressRow} ${isEditing ? styles.addressRowEditing : styles.addressRowView}`}
                      >
                        {isEditing ? (
                          <>
                            <label className={styles.inputLabel}>Mobile no. :</label>
                            <input
                              className={styles.inputAddress}
                              name="tel_no"
                              value={formData.tel_no}
                              onChange={handleChange}
                              placeholder="Phone / Mobile"
                            />
                          </>
                        ) : (
                          <div className={styles.content}>{formData.tel_no}</div>
                        )}
                      </div>
                    )}

                    {(isEditing || formData.tin) && (
                      <div
                        className={`${styles.addressRow} ${isEditing ? styles.addressRowEditing : styles.addressRowView}`}
                      >
                        {isEditing ? (
                          <>
                            <label className={styles.inputLabel}>Tax no. :</label>
                            <input
                              className={styles.inputAddress}
                              name="tin"
                              value={formData.tin}
                              onChange={handleChange}
                              placeholder="GST / TIN"
                            />
                          </>
                        ) : (
                          <div className={styles.content}>{formData.tin}</div>
                        )}
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          {(isEditing || formData.header) && (
            <div className={`${styles.addressRow} ${isEditing ? styles.addressRowEditing : styles.addressRowView}`}>
              {isEditing ? (
                <>
                  <label className={styles.inputLabel}>Header :</label>
                  <input
                    className={styles.inputAddress}
                    name="header"
                    value={formData.header}
                    onChange={handleChange}
                    placeholder="Header"
                  />
                </>
              ) : (
                <div className={styles.invoiceTitle}>{formData.header}</div>
              )}
            </div>
          )}

          <div className={styles.divider} />
          <div className={styles.divider} />

          <table className={styles.itemsTable}>
            <thead>
              <tr>
                <th>#</th>
                <th className={styles.itemName}>Item Name</th>
                <th>Qty</th>
                <th>Rate</th>
                <th className={styles.alignRight} style={{ textAlign: "end" }}>
                  Amount
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={it.id}>
                  <td>{i + 1}</td>
                  <td className={styles.itemName}>{it.name}</td>
                  <td>{it.qty}</td>
                  <td>{roundOff(it.rate)}</td>
                  <td className={styles.alignRight}>{roundOff(it.qty * it.rate)}</td>
                </tr>
              ))}
              <tr className={styles.summaryRow}>
                <td colSpan="3">3 Items</td>
                <td className={styles.alignRight}> </td>
                <td className={styles.alignRight}>{roundOff(subtotal)}</td>
              </tr>
              <tr className={styles.rightRow}>
                <td colSpan="4" className={styles.rightLabel}>
                  Total before tax :
                </td>
                <td>{roundOff(subtotal)}</td>
              </tr>
              <tr className={styles.rightRow}>
                <td colSpan="4" className={styles.rightLabel}>
                  CGST :
                </td>
                <td>{roundOff(cgst)}</td>
              </tr>
              <tr className={styles.rightRow}>
                <td colSpan="4" className={styles.rightLabel}>
                  SGST :
                </td>
                <td>{roundOff(sgst)}</td>
              </tr>
              <tr className={styles.rightRow}>
                <td colSpan="4" className={styles.rightLabel}>
                  CESS :
                </td>
                <td>{roundOff(cess)}</td>
              </tr>
              <tr className={styles.rightRow}>
                <td colSpan="4" className={styles.rightLabel}>
                  Rounding :
                </td>
                <td>{roundOff(rounding)}</td>
              </tr>
            </tbody>
          </table>

          <div className={styles.divider} />
          <div className={styles.divider} />

          <div className={styles.subtotalCard}>
            <div className={styles.amount}>
              <div className={styles.amountContent}>SUB TOTAL</div>
              <div className={styles.amountContent}>{roundOff(total)}</div>
            </div>

            <div className={styles.inclusive}>INCLUSIVE OF ALL TAXES</div>
          </div>

          <div className={`${styles.noteSection} ${isEditing ? styles.addressRowEditing : styles.addressRowView}`}>
            {isEditing ? (
              <>
                <label className={styles.inputLabel}>Note (Bank Details) :</label>
                <textarea
                  className={styles.inputTextarea}
                  name="note"
                  value={formData.note}
                  onChange={handleChange}
                  placeholder="Enter Bank and UPI Details"
                  rows={4}
                />
              </>
            ) : (
              <div className={styles.noteContent}>{formData.note}</div>
            )}
          </div>

          <div className={styles.divider} />
          <div className={styles.divider} />

          <div className={`${styles.footer} ${isEditing ? styles.addressRowEditing : styles.footerRowView}`}>
            <div style={{ width: "100%" }}>
              {isEditing ? (
                <>
                  <div
                    className={`${styles.addressRow} ${isEditing ? styles.addressRowEditing : styles.addressRowView}`}
                  >
                    <label className={styles.inputLabel}>Footer 1 :</label>
                    <input
                      className={styles.inputAddress}
                      name="footer_1"
                      value={formData.footer_1}
                      onChange={handleChange}
                      placeholder="Footer line 1"
                    />
                  </div>

                  <div
                    className={`${styles.addressRow} ${isEditing ? styles.addressRowEditing : styles.addressRowView}`}
                  >
                    <label className={styles.inputLabel}>Footer 2 :</label>
                    <input
                      className={styles.inputAddress}
                      name="footer_2"
                      value={formData.footer_2}
                      onChange={handleChange}
                      placeholder="Footer line 2"
                    />
                  </div>
                </>
              ) : (
                <div style={{ width: "100%" }}>
                  <div className={styles.footerLine}>{formData.footer_1}</div>
                  <div className={styles.footerLine}>{formData.footer_2}</div>
                </div>
              )}
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "center" }}>
            {upiId && <p className={styles.alignCenter}>UPI ID: {upiId || "-"}</p>}
          </div>
        </div>
      )}{" "}
    </>
  );
}

export default InvoiceSample;
